import { useEffect, useMemo, useState } from 'react'
import { useEquipmentScope } from '../equipmentScope'
import { ResourceFilter } from '../ResourceFilter'
import { Plus, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useMyList, useTaskStats } from '@/hooks/useMyList'
import { useReservationTasks } from '@/hooks/useReservationTasks'
import { isReservationRow } from '@/lib/reservationTasks'
import { TaskDateRange } from './TaskDateRange'
import {
  allTimeWindow,
  parseIsoDay,
  presetRange,
  rangeLabel,
  shiftAnchor,
  shiftDisplayRange,
  toQueryRange,
  type DisplayRange,
  type RangePreset,
} from '@/lib/taskDateRange'
import {
  ALL_TASKS_LIMIT,
  hiddenReasons,
  matchesFilter,
  matchesSearch,
  subjectQuery,
  worklistQuery,
  type TaskSubject,
} from '@/lib/taskFilters'
import { keepRowOrder } from '@/lib/keepRowOrder'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { MyListTable } from './MyListTable'
import { PlannerPagination } from '../PlannerPagination'
import { NewEventModal } from './NewEventModal'
import { StartEventModal } from './StartEventModal'
import { EndEventModal } from './EndEventModal'
import { UndoEndModal } from './UndoEndModal'
import { EventDetailsDialog } from './EventDetailsDialog'
import { EditEventModal } from './EditEventModal'
import { useAuth } from '@/providers/AuthProvider'
import { useAssignableUsers } from '@/hooks/useAssignableUsers'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { eventMeta, type MaintenanceTask, type TaskFilter } from '@/types/maintenanceTask'
import { useEventTypeCatalog } from '@/hooks/useEventTypes'
import { compareWorklistRows } from '@/lib/taskOrder'
import { useResourceFilter } from '@/hooks/useResourceFilter'
import { useTaskForm } from '@/hooks/useTaskForm'
import { ProcedurePreviewDialog } from '@/components/procedures/ProcedurePreviewDialog'

const FILTERS: { id: TaskFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'due', label: 'Due' },
  { id: 'started', label: 'Started' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'pm', label: 'PM' },
  { id: 'cm', label: 'CM' },
  { id: 'process', label: 'Process' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'unassigned', label: 'Unassigned' },
]

const LEGEND = [
  'preventive_maintenance',
  'corrective_maintenance',
  'corrective_action',
  'cleaning',
  'calibration',
  'inspection',
  'verification',
  'process',
]

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className='flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm'>
      <span className='h-9 w-1.5 shrink-0 rounded-full' style={{ backgroundColor: color }} />
      <div className='min-w-0'>
        <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>{label}</p>
        <p className='text-2xl font-bold leading-tight text-gray-900'>{value}</p>
      </div>
    </div>
  )
}

export function MyListTab() {
  const scope = useEquipmentScope()
  const { copy } = scope
  // Date scope. `anchor` is the period the arrows step; `custom` holds a
  // hand-typed range and wins over the preset while it is set.
  const [preset, setPreset] = useState<RangePreset>('month')
  const [anchor, setAnchor] = useState(() => new Date())
  const [custom, setCustom] = useState<DisplayRange>(null)

  // An inverted range is treated as "not a range yet" by BOTH feeds. Letting it
  // through produced opposite readings of the same input — the events query saw
  // no filter and returned everything, while the reservations query went
  // disabled and returned nothing — so the board showed the tenant's entire
  // maintenance history and zero bookings, with nothing saying the range was
  // rejected.
  const rangeInvalid = custom !== null && custom.to < custom.from
  // What the picker shows — the typed dates stay visible even while invalid, so
  // the user can see and correct what they entered.
  const displayRange = useMemo(
    () => custom ?? presetRange(preset, anchor),
    [custom, preset, anchor],
  )
  // What the feeds query. Null while invalid, so neither one acts on it.
  const range = rangeInvalid ? null : displayRange
  // Declared here, ahead of the feeds, because the Overdue chip changes what
  // the events feed asks for (see `worklistQuery`).
  const [filter, setFilter] = useState<TaskFilter>('all')
  // Whose tasks (z8nrz7fgzw): the viewer's own unless they are Super Admin or
  // a supervisor. Server-side, so the list, its paging and the cards agree.
  const [resourceId, setResourceId] = useResourceFilter()
  // Inventory only (z8nrz7fjzc): storage units' tasks, one material type's, or all.
  const [subject, setSubject] = useState<TaskSubject>('all')
  // Inventory lists only what inventory actions made (z8nrz7fjzc): its storage
  // units' maintenance — cleaning, PM, inspections — stays on the Equipment page.
  const subjects = useMemo(
    () => ({
      ...subjectQuery(subject, scope.purposes, scope.materialTypes, scope.orders),
      ...(scope.tasksFromActions ? { triggered: true } : {}),
    }),
    [subject, scope.purposes, scope.materialTypes, scope.orders, scope.tasksFromActions],
  )
  // One conversion for both feeds, so they can never disagree about the period.
  const period = useMemo(() => toQueryRange(range), [range])
  // Every feed on this tab is this page's share: the Cleaning page's worklist is
  // room cleaning only; the Equipment page's never holds a room (z8nrz7ewt2).
  const query = useMemo(
    () => ({ ...worklistQuery(range, filter), ...subjects, assigneeId: resourceId ?? undefined }),
    [range, filter, subjects, resourceId],
  )
  // The reservations endpoint has no "everything" mode, so All still needs a
  // window. Anchored to the same date the arrows use, not to Date.now(), so the
  // query key is stable across renders.
  const reservationWindow = useMemo(
    () => (range ? { from: period.from ?? null, to: period.to ?? null } : allTimeWindow(anchor)),
    [range, period, anchor],
  )

  const {
    tasks: eventTasks,
    start,
    end,
    undoEnd,
    createEvent,
    updateEvent,
    rescheduleEvent,
    isLoading,
    error,
  } = useMyList(query)
  // The stat cards count the SAME tasks the list holds — its dates, items and
  // Resource filter — so they add up to its total (z8nrz7fjzc): Due Today, In
  // Progress, Overdue, Completed and Not Started split it with no overlap. Counted by the server, so the page still never downloads
  // the tenant's whole history to show them (z8nrz7f9u6). The chips and the
  // search narrow the rows, not the cards: the cards are what the chips pick from.
  const stats = useTaskStats(
    useMemo(
      () => ({ ...period, ...subjects, assigneeId: resourceId ?? undefined }),
      [period, subjects, resourceId],
    ),
  )
  // All is bounded by count (`worklistQuery` asks for one over the limit): the
  // latest tasks by due date, and a note when older ones are left out.
  const truncated = range === null && filter !== 'overdue' && eventTasks.length > ALL_TASKS_LIMIT
  const listedTasks = useMemo(
    () => (truncated ? eventTasks.slice(0, ALL_TASKS_LIMIT) : eventTasks),
    [truncated, eventTasks],
  )
  // Process-step bookings occupy the same machines, so they belong in the same
  // worklist. Fetched separately (see useReservationTasks) so nothing read-only
  // leaks into the Team Schedule, which also reads useMyList.
  const { tasks: reservationTasks, error: reservationsError } = useReservationTasks(
    reservationWindow.from,
    reservationWindow.to,
    scope.purposes,
  )
  // A process booking is nobody's task, so one person's view leaves them out.
  // Nor are they on a material, so a material type's view leaves them out too.
  // Nor are they an inventory action's, so Inventory leaves them out.
  const bookings = useMemo(
    () => (resourceId || !subjects.purposes || scope.tasksFromActions ? [] : reservationTasks),
    [reservationTasks, resourceId, subjects.purposes, scope.tasksFromActions],
  )
  const tasks = useMemo(() => [...listedTasks, ...bookings], [listedTasks, bookings])
  const { email, user } = useAuth()
  // Assign from the row (z8nrz7f9u5): the same right the Edit dialog needs.
  const { can } = usePermission()
  const canAssign = can(PERMISSIONS.equipmentExecute)
  const { users: assignableUsers } = useAssignableUsers()

  const applyPreset = (next: RangePreset) => {
    setCustom(null)
    setPreset(next)
    // Presets are read relative to now, not to wherever the arrows were left.
    setAnchor(new Date())
  }

  const shift = (direction: 1 | -1) => {
    if (custom) setCustom(shiftDisplayRange(custom, direction))
    else setAnchor((a) => shiftAnchor(preset, a, direction))
  }

  // Execution modals only ever open on a real MaintenanceEvent. The table
  // already withholds those controls from reservation rows, but the lookup is
  // guarded too so a later change to the table can't quietly hand one through —
  // there is no event behind it, so every action would 404. Details (onInfo) is
  // deliberately not guarded: reading a booking is exactly what it is for.
  const mutable = (id: string): MaintenanceTask | null => {
    if (isReservationRow(id)) return null
    return tasks.find((t) => t.id === id) ?? null
  }
  const [search, setSearch] = useState('')
  const catalog = useEventTypeCatalog()
  const [newEventOpen, setNewEventOpen] = useState(false)
  const [startTask, setStartTask] = useState<MaintenanceTask | null>(null)
  // What the unit still holds, when Start was refused for it: the dialog then
  // asks for the override reason up front.
  const [startHeld, setStartHeld] = useState<string | null>(null)
  const openStart = (task: MaintenanceTask | null, holdsStock: string | null = null) => {
    setStartTask(task)
    setStartHeld(holdsStock)
  }
  const [endTask, setEndTask] = useState<MaintenanceTask | null>(null)
  const [undoTask, setUndoTask] = useState<MaintenanceTask | null>(null)
  const [infoTask, setInfoTask] = useState<MaintenanceTask | null>(null)
  const [editTask, setEditTask] = useState<MaintenanceTask | null>(null)
  const [previewTask, setPreviewTask] = useState<MaintenanceTask | null>(null)

  // A task with a form (z8nrz7fgzw): Start goes straight to its run.
  const taskForm = useTaskForm()

  // The task just created, kept only while something is hiding it.
  //
  // Creating a task the current view excludes saves it and shows nothing: the
  // worklist opens on the current month, so a task due next year lands nowhere,
  // and a chip or a search term hides one just as completely. The silence reads
  // as a failed save rather than a filtered view — so hold the row and say
  // which control is responsible.
  const [createdTask, setCreatedTask] = useState<MaintenanceTask | null>(null)
  const createEventAndLocate: typeof createEvent = (payload, fallback) => {
    createEvent(payload, fallback, setCreatedTask)
  }
  // DERIVED, not stored: the notice recomputes against the live controls, so it
  // clears itself the moment the row comes into view — whether from "Show it"
  // or from the user's own picker — and can never outlive what it describes.
  const createdHiddenBy = createdTask ? hiddenReasons(createdTask, range, filter, search, catalog, resourceId, subject) : []
  // Clear every control that is hiding it, not just the first: leaving one in
  // place would land the user back on an empty list.
  const showCreated = () => {
    if (!createdTask) return
    if (createdHiddenBy.includes('range')) {
      setCustom(null)
      setPreset('month')
      setAnchor(parseIsoDay(createdTask.dueDate))
    }
    if (createdHiddenBy.includes('filter')) setFilter('all')
    if (createdHiddenBy.includes('search')) setSearch('')
    if (createdHiddenBy.includes('resource')) setResourceId(null)
    if (createdHiddenBy.includes('subject')) setSubject('all')
  }

  // Inventory's tasks are made by inventory actions (z8nrz7fjzc): nothing on
  // them is preventive or corrective maintenance, nor a process booking, so
  // those chips do not apply.
  const chips = scope.tasksFromActions ? FILTERS.filter((f) => !['pm', 'cm', 'process'].includes(f.id)) : FILTERS

  // The order the rows were in when a due date was edited in place, held so the
  // edited row stays put instead of jumping to its new date's place; any
  // change to what the list shows lets it sort again (see keepRowOrder).
  const [heldOrder, setHeldOrder] = useState<string[] | null>(null)
  useEffect(() => {
    setHeldOrder(null)
  }, [filter, search, preset, custom, anchor, resourceId, subject])

  const rows = useMemo(
    () =>
      keepRowOrder(
        tasks
          .filter((t) => matchesFilter(t, filter))
          .filter((t) => matchesSearch(t, search, catalog))
          .sort(
            (a, b) =>
              compareWorklistRows(a, b, catalog),
          ),
        heldOrder,
      ),
    [tasks, filter, search, catalog, heldOrder],
  )

  // Paged like the planner tables. A new range, chip or search starts again at
  // page 1; a page past the end (rows ended or filtered away) shows the last.
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(15)
  useEffect(() => {
    setPage(1)
  }, [filter, search, preset, custom, anchor, resourceId, subject])
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const pageRows = useMemo(
    () => rows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [rows, currentPage, pageSize],
  )
  // A task just created opens on the page that holds it.
  const createdIndex = createdTask ? rows.findIndex((t) => t.id === createdTask.id) : -1
  useEffect(() => {
    if (createdIndex >= 0) setPage(Math.floor(createdIndex / pageSize) + 1)
  }, [createdIndex, pageSize])

  return (
    <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
      {/* Header */}
      <div className='flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 p-4'>
        <div>
          <h2 className='text-lg font-semibold tracking-tight text-gray-900'>Tasks</h2>
          <p className='mt-0.5 text-xs text-muted-foreground'>
            {copy.worklist} ·{' '}
            <span className='font-medium text-gray-700'>{stats.dueTodayAll} due today</span>
          </p>
        </div>
        {/* Inventory's tasks come from inventory actions, not New Event (z8nrz7fjzc). */}
        {!scope.tasksFromActions && (
          <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
            <div className='hidden flex-wrap items-center gap-x-3 gap-y-1 lg:flex'>
              <span className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                Event types
              </span>
              {/* The tenant's own event types, as the planner lists them (z8nrz7fb9k). */}
              {(catalog.size > 0 ? [...catalog.keys()] : LEGEND).map((code) => (
                <span key={code} className='inline-flex items-center gap-1.5 text-xs text-gray-600'>
                  <span className='h-2 w-2 rounded-full' style={{ backgroundColor: eventMeta(code, catalog).color }} />
                  {eventMeta(code, catalog).label}
                </span>
              ))}
            </div>
            <button
              type='button'
              onClick={() => setNewEventOpen(true)}
              className='inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 active:scale-[0.98]'
            >
              <Plus className='h-4 w-4' />
              New Event
            </button>
          </div>
        )}
      </div>

      {/* Stat cards: the list's own tasks. Due Today + In Progress + Overdue +
          Completed + Not Started (+ Process bookings) = the list's total. */}
      <div
        className={cn(
          'grid grid-cols-2 gap-3 p-4 sm:grid-cols-3',
          bookings.length > 0 ? 'lg:grid-cols-6' : 'lg:grid-cols-5',
        )}
        data-testid='task-stats'
      >
        <StatCard label='Due Today' value={stats.dueToday} color='#2563eb' />
        <StatCard label='In Progress' value={stats.inProgress} color='#d97706' />
        <StatCard label='Overdue' value={stats.overdue} color='#dc2626' />
        <StatCard label='Completed' value={stats.completed} color='#16a34a' />
        <StatCard label='Not Started' value={stats.notStarted} color='#64748b' />
        {bookings.length > 0 && <StatCard label='Process' value={bookings.length} color='#7c3aed' />}
      </div>

      {/* Date scope — drives both the maintenance-event feed and the
          reservation window, so the two always cover the same period. */}
      <div className='flex flex-wrap items-center gap-2.5 border-t border-gray-100 px-4 pb-3 pt-3'>
        <TaskDateRange
          preset={preset}
          range={displayRange}
          isCustom={custom !== null}
          onPreset={applyPreset}
          onShift={shift}
          onCustom={(from, to) => setCustom({ from, to })}
        />
        {filter === 'overdue' && (
          <span className='text-[11px] font-medium text-gray-500'>Overdue shows all dates.</span>
        )}
        {rangeInvalid && (
          <span className='text-[11px] font-medium text-amber-600'>
            End date is before the start date — showing all dates until you fix it.
          </span>
        )}
      </div>

      {/* Toolbar */}
      <div className='flex flex-wrap items-center gap-2.5 px-4 pb-3'>
        <div className='relative min-w-[240px] flex-1 max-w-sm'>
          <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            type='search'
            name='mylist-search'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={copy.taskSearch}
            className='pl-9'
            // A password field opened in a modal makes browsers/password managers
            // hunt the page for a "username" field to autofill; without these
            // guards they type the signed-in email into this search box, wiping
            // the worklist behind the modal.
            autoComplete='off'
            data-1p-ignore='true'
            data-lpignore='true'
            data-form-type='other'
          />
        </div>
        <ResourceFilter value={resourceId} onChange={setResourceId} />
        {scope.materialTypes && (
          <Select value={subject} onValueChange={setSubject}>
            <SelectTrigger className='h-9 w-44 text-xs' title='Items'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All items</SelectItem>
              <SelectItem value='storage'>Storage units</SelectItem>
              {scope.orders && <SelectItem value='orders'>Purchase orders</SelectItem>}
              {scope.materialTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  {`${t}s`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className='flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-0.5'>
          {chips.map((f) => (
            <button
              key={f.id}
              type='button'
              onClick={() => setFilter(f.id)}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                filter === f.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-gray-600 hover:bg-white hover:text-gray-900',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Worklist */}
      <div className='px-4 pb-4'>
        {createdTask && createdHiddenBy.length > 0 && (
          <div className='mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800'>
            <span>
              Task created, due{' '}
              <span className='font-semibold'>
                {parseIsoDay(createdTask.dueDate).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>{' '}
              — hidden by{' '}
              {createdHiddenBy
                .map((reason) =>
                  reason === 'range'
                    ? `the dates on screen (${rangeLabel(preset, displayRange)})`
                    : reason === 'filter'
                      ? `the ${FILTERS.find((f) => f.id === filter)?.label ?? filter} filter`
                      : reason === 'resource'
                        ? 'the Resource filter'
                        : reason === 'subject'
                          ? 'the item filter'
                          : 'the search box',
                )
                .join(' and ')}
              , so it is not in the list below.
            </span>
            <button
              type='button'
              className='font-semibold underline underline-offset-2 hover:text-amber-900'
              onClick={showCreated}
            >
              Show it
            </button>
            <button
              type='button'
              className='ml-auto text-amber-600 hover:text-amber-900'
              onClick={() => setCreatedTask(null)}
            >
              Dismiss
            </button>
          </div>
        )}
        {truncated && (
          <p className='mb-2 text-[11px] font-medium text-amber-700' data-testid='tasks-truncated'>
            Showing the latest {ALL_TASKS_LIMIT.toLocaleString()} tasks by due date. Pick a date range to see older
            ones.
          </p>
        )}
        {taskForm.error && (
          <p className='mb-2 text-xs font-medium text-red-600' role='alert'>
            {taskForm.error}
          </p>
        )}
        <MyListTable
          tasks={pageRows}
          onStart={(id) => {
            const task = mutable(id)
            if (!task?.procedureTemplate) openStart(task)
            else void taskForm.start(task).then((r) => typeof r === 'object' && openStart(task, r.holdsStock))
          }}
          onPreviewForm={(id) => setPreviewTask(tasks.find((t) => t.id === id) ?? null)}
          onEnd={(id) => {
            // A task with a form ends by completing its form, not by the End dialog.
            const task = mutable(id)
            if (task?.procedureTemplate) void taskForm.open(task)
            else setEndTask(task)
          }}
          onUndo={(id) => setUndoTask(mutable(id))}
          onInfo={(id) => setInfoTask(tasks.find((t) => t.id === id) ?? null)}
          onEdit={(id) => setEditTask(mutable(id))}
          // The Due Date column, edited in place (overdue tasks especially).
          // Same permission as the server's edit route, equipment.execute.
          onReschedule={
            canAssign
              ? async (id, dueDate) => {
                  if (!mutable(id)) return
                  setHeldOrder((held) => held ?? rows.map((r) => r.id))
                  await rescheduleEvent(id, dueDate, email ?? undefined)
                }
              : undefined
          }
          assign={
            canAssign
              ? {
                  users: assignableUsers,
                  meId: user?.userId,
                  onAssign: (id, assigneeId) => {
                    if (mutable(id)) updateEvent(id, { assigneeId: assigneeId ?? '', actor: email ?? undefined })
                  },
                }
              : undefined
          }
        />
        {rows.length > 0 && (
          <PlannerPagination
            page={currentPage}
            pageSize={pageSize}
            total={rows.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        )}
        {error ? (
          <p className='mt-3 text-[11px] text-red-500'>Couldn’t load tasks: {error}</p>
        ) : isLoading ? (
          <p className='mt-3 text-[11px] text-gray-400'>Loading tasks…</p>
        ) : null}
        {/* Reported separately: maintenance tasks can load fine while the
            reservation layer fails, and silently showing a short list would
            read as "this machine is free". */}
        {reservationsError && (
          <p className='mt-1 text-[11px] text-red-500'>
            Couldn’t load process reservations: {reservationsError}
          </p>
        )}
      </div>

      {!scope.tasksFromActions && (
        <NewEventModal
          open={newEventOpen}
          onClose={() => setNewEventOpen(false)}
          onCreate={createEventAndLocate}
        />
      )}
      <StartEventModal
        task={startTask}
        holdsStock={startHeld}
        onClose={() => openStart(null)}
        onConfirm={async (id, payload) => {
          // A task with a form only reaches this dialog when its unit holds
          // stock: once started, it goes on to its form.
          const task = startTask
          await start(id, payload, task?.procedureTemplate ? () => void taskForm.open(task) : undefined)
          openStart(null)
        }}
      />
      <EndEventModal
        task={endTask}
        onClose={() => setEndTask(null)}
        onConfirm={async (id, payload) => {
          await end(id, payload)
          setEndTask(null)
        }}
      />
      <UndoEndModal
        task={undoTask}
        onClose={() => setUndoTask(null)}
        onConfirm={(id, payload) => {
          undoEnd(id, payload)
          setUndoTask(null)
        }}
      />
      <EventDetailsDialog task={infoTask} onClose={() => setInfoTask(null)} />
      <ProcedurePreviewDialog
        procedure={previewTask?.procedureTemplate ?? null}
        onClose={() => setPreviewTask(null)}
        context={previewTask ? `${previewTask.equipmentId} · ${eventMeta(previewTask.eventType, catalog).label}` : undefined}
      />
      <EditEventModal
        task={editTask}
        onClose={() => setEditTask(null)}
        onSave={(id, patch) => {
          updateEvent(id, { ...patch, actor: email ?? undefined })
          setEditTask(null)
        }}
      />
    </div>
  )
}
